import React, { useState, useMemo } from 'react';
import { ErrorBoundary } from './src/components/ErrorBoundary';
import { useSyncState, registerDocMerger } from './src/hooks/useSyncState';
import { mergeWeeklyPlans } from './src/utils/weeklyPlanMerge';
import { mergeGlobalProfiles } from './src/utils/profileMerge';
import { setupPendingPhotosSync } from './src/utils/pendingPhotosQueue';
import { seedDatabase } from './src/seedData';

// Registrar fusionadores: garantizan que ningún cambio manual se pierda ni sea revertido por clientes desactualizados
registerDocMerger('weeklyPlans', mergeWeeklyPlans);
registerDocMerger('globalProfiles', mergeGlobalProfiles);
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { supabase } from './src/supabaseClient';
import { clearMsalCacheForce } from './src/authConfig';
import LoginScreen from './src/components/LoginScreen';
import DashboardScreen from './pages/Dashboard';
import BudgetScreen from './pages/Budget';
import PlanningScreen from './pages/Planning';
import QualityScreen from './pages/Quality';
import PaymentsScreen from './pages/Payments';
import EDTScreen from './pages/EDT';
import ProjectSelection from './pages/ProjectSelection';
import PreventionScreen from './pages/Prevention';
import { AIChatWidget } from './src/components/ui/AIChatWidget';
import ReportBugWidget from './src/components/ui/ReportBugWidget';
import { useMsal } from '@azure/msal-react';
import WaitingRoom from './src/pages/WaitingRoom';
import GlobalAdminPanel from './src/pages/GlobalAdminPanel';
import { NotificationBell } from './src/components/ui/NotificationBell';
import { useUserNotifications } from './src/hooks/useUserNotifications';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { checkForUpdate, RemoteVersionInfo, VersionInfo } from './src/utils/versionCheck';
import { Browser } from '@capacitor/browser';
import { MandatoryUpdateScreen } from './src/components/mobile/MandatoryUpdateScreen';
import { KlDialog } from './src/components/mobile/modal/KlDialog';
import { useAppVersionDisplay } from './src/hooks/useAppVersionDisplay';
import { isMigratedMobileRoute } from './src/components/mobile/mobileRouteConfig';
import { MobileBottomNav } from './src/components/mobile/MobileBottomNav';
import { MobileQuickActions } from './src/components/mobile/MobileQuickActions';
import { MobileAppHeader } from './src/components/mobile/MobileAppHeader';
import { modalStack } from './src/components/mobile/modal/modalStack';
const ModalSystemDemo = import.meta.env.DEV
    ? React.lazy(() => import('./src/components/mobile/modal/ModalSystemDemo'))
    : null;

const RouteChangeModalCloser: React.FC = () => {
    const location = useLocation();
    const prevPathnameRef = React.useRef(location.pathname);
    React.useEffect(() => {
        // Solo cerrar ventanas si la ruta realmente cambió por una navegación del usuario
        if (prevPathnameRef.current !== location.pathname) {
            prevPathnameRef.current = location.pathname;
            modalStack.closeAll();
        }
    }, [location.pathname]);
    return null;
};

if (typeof window !== 'undefined') {
    (window as any).seedSupabase = seedDatabase;
}

// --- Shared Types ---
import {
    Task, Level, ResourceProject, ResourceItem, BudgetFolder, BudgetItem, ApuItem, PhysicalUnit,
    Responsible, PermissionSet, Profile, ProjectInfo, CompanyConfig, ProtocolItem, Reviewer,
    ReviewerGroup, ProtocolConfig, ChecklistStatus, ChecklistValue, ProtocolHistoryEntry,
    ProtocolState, WeeklyAllocation, WeeklyPlan, Constraint, LookaheadPlan, ProjectProgressSnapshot,
    CNCCause, CNCGroup, ConstraintDefinition, ConstraintGroup, GanttBaseline, SubcontractType,
    Subcontractor, UnitDetail, StatementItem, Contract, PaymentStatement, Activity, GlobalUser,
    AppNotification, Incidence, MasterProtocol, WorkGroupTemplate
} from './src/types';

const HelmetIcon = ({ className = "size-6" }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 4C7.58 4 4 7.58 4 12v2h16v-2c0-4.42-3.58-8-8-8z" />
        <path d="M11 4.5h2v8h-2z" opacity="0.15" fill="black" />
        <path d="M2 15c0-.55.45-1 1-1h18c.55 0 1 .45 1 1s-.45 1-1 1H3c-.55 0-1-.45-1-1z" />
    </svg>
);

const Sidebar = ({ onBack, currentUser, onLogout, hasPermission, companyConfig, isNative, notifications, setNotifications, isPlatformAdmin }: { onBack: () => void, currentUser?: GlobalUser, onLogout: () => void, hasPermission: (module: string, action: 'view' | 'edit') => boolean, companyConfig: CompanyConfig, isNative?: boolean, notifications?: AppNotification[], setNotifications?: React.Dispatch<React.SetStateAction<AppNotification[]>>, isPlatformAdmin?: boolean }) => {
    const location = useLocation();
    if (isNative) return null;
    const isActive = (path: string) => location.pathname === path;

    const isAdmin = currentUser?.globalRole === 'Administrador' || currentUser?.globalRole === 'SYSADMIN' || Boolean(isPlatformAdmin);
    const navItems = [
        { path: "/", label: "Dashboard", icon: "dashboard", show: true },
        { path: "/edt", label: "EDT", icon: "account_tree", show: hasPermission('mod_edt', 'view') },
        { path: "/planning", label: "Planificación", icon: "calendar_month", show: hasPermission('mod_planificacion', 'view') },
        { path: "/quality", label: "Control de Calidad", icon: "fact_check", show: hasPermission('mod_calidad', 'view') },
        { path: "/payments", label: "Subcontratos", icon: "handshake", show: hasPermission('mod_subcontratos', 'view') },
        { path: "/prevention", label: "Prevención (SSOMA)", icon: "helmet_safety", show: hasPermission('mod_prevencion', 'view') },
        { path: "/reports", label: "Reportes", icon: "bar_chart", show: hasPermission('mod_reportes', 'view') },
        { path: "/data-intelligence", label: "Inteligencia de Datos", icon: "psychology", show: hasPermission('mod_data_intelligence', 'view') },
    ].filter(i => i.show !== false);

    return (
        <aside className="hidden md:flex h-full w-64 flex-col border-r border-[#e7ecf3] bg-white dark:bg-[#1a2634] dark:border-[#2a3644] flex-shrink-0 z-50">
            <div className="flex h-full flex-col justify-between p-4">
                <div className="flex flex-col gap-6">
                    <div className="flex items-center gap-3 px-2">
                        <button
                            onClick={onBack}
                            className="flex items-center justify-center size-8 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-all mr-1"
                            title="Volver a Selección de Proyecto"
                        >
                            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                        </button>
                        <div className="flex items-center justify-center rounded-xl size-10 flex-shrink-0 overflow-hidden bg-white border border-slate-100 dark:border-slate-800">
                            <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Logo" className="w-full h-full object-contain p-1" />
                        </div>
                        <div className="flex flex-col flex-1 truncate">
                            <h1 className="text-base font-bold leading-normal text-slate-900 dark:text-white truncate">K-LyDapp</h1>
                            <p className="text-xs font-normal text-slate-500 dark:text-slate-400 truncate">Gestionamos tu Obra</p>
                        </div>
                    </div>
                    <nav className="flex flex-col gap-2">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive(item.path)
                                    ? "bg-primary/10 text-primary"
                                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                                    }`}
                            >
                                {item.icon === 'helmet_safety' ? (
                                    <HelmetIcon className="size-[24px] flex-shrink-0" />
                                ) : (
                                    <span className="material-symbols-outlined text-[24px] flex-shrink-0">{item.icon}</span>
                                )}
                                <span className="text-sm font-medium">{item.label}</span>
                            </Link>
                        ))}
                    </nav>
                </div>
                <div className="flex flex-col gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">

                    {currentUser && (
                        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between px-2">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className="h-9 w-9 flex-shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs uppercase border border-primary/20">
                                    {(() => {
                                        if (!currentUser) return 'U';
                                        if (currentUser.name && !currentUser.name.toLowerCase().includes('usuario invitado') && currentUser.name !== currentUser.email) {
                                            return (currentUser.name.split(' ')[0]?.[0] || '') + (currentUser.name.split(' ')[1]?.[0] || '');
                                        }
                                        return currentUser.email.substring(0, 2).toUpperCase();
                                    })()}
                                </div>
                                <div className="flex flex-col truncate">
                                    <span className="text-sm font-bold text-slate-800 dark:text-white truncate">{currentUser.name}</span>
                                    <span className="text-[10px] font-medium text-slate-500 truncate uppercase">{currentUser.globalRole}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                {notifications && setNotifications && (
                                    <NotificationBell
                                        notifications={notifications}
                                        setNotifications={setNotifications}
                                        currentUser={currentUser}
                                    />
                                )}
                                <button 
                                    onClick={onLogout}
                                    className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                    title="Cerrar Sesión"
                                >
                                    <span className="material-symbols-outlined text-[20px]">logout</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

// (Notification components moved to src/components/ui/NotificationBell.tsx)

const MobileHeader = ({ onBack, currentUser, onToggleMenu, notifications, setNotifications, companyConfig, isNative, onLogout }: { onBack: () => void, currentUser?: GlobalUser, onToggleMenu: () => void, notifications?: AppNotification[], setNotifications?: React.Dispatch<React.SetStateAction<AppNotification[]>>, companyConfig: CompanyConfig, isNative?: boolean, onLogout?: () => void }) => {
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const { fullVersionString } = useAppVersionDisplay();
    const userInitial = (() => {
        if (!currentUser) return 'U';
        if (currentUser.name && !currentUser.name.toLowerCase().includes('usuario invitado') && currentUser.name !== currentUser.email) {
            return (currentUser.name.split(' ')[0]?.[0] || '') + (currentUser.name.split(' ')[1]?.[0] || '');
        }
        return currentUser.email.substring(0, 2).toUpperCase();
    })();

    return (
        <header 
            className={`${isNative ? 'flex' : 'md:hidden flex'} items-center justify-between bg-white dark:bg-[#1a2634] border-b border-slate-200 dark:border-slate-800 px-4 flex-shrink-0 z-40`}
            style={{
                height: "calc(48px + var(--kl-safe-top, env(safe-area-inset-top, 0px)))",
                paddingTop: "var(--kl-safe-top, env(safe-area-inset-top, 0px))"
            }}
        >
            <div className="flex items-center gap-3">
                <button onClick={onBack} className="p-1 -ml-1 text-slate-500 hover:text-primary transition-colors">
                    <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center rounded-lg size-8 flex-shrink-0 overflow-hidden bg-white border border-slate-100 dark:border-slate-800">
                        <img src={'/assets/play_store_icon.png'} alt="Icono K-LyDapp" className="w-full h-full object-contain p-0.5 rounded" />
                    </div>
                    <span className="font-bold text-sm text-slate-800 dark:text-white">K-LyDapp</span>
                </div>
            </div>
            
            <div className="flex items-center gap-3">
                {notifications && setNotifications && (
                    <NotificationBell notifications={notifications} setNotifications={setNotifications} currentUser={currentUser} />
                )}
                {currentUser && (
                    <button 
                        type="button"
                        onClick={() => setIsUserModalOpen(true)}
                        aria-label="Perfil y versión de la aplicación"
                        className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[10px] uppercase border border-primary/20 cursor-pointer"
                    >
                        {userInitial}
                    </button>
                )}
                {onLogout && (
                    <button 
                        onClick={onLogout}
                        className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Cerrar Sesión"
                    >
                        <span className="material-symbols-outlined text-[20px]">logout</span>
                    </button>
                )}
            </div>

            {isUserModalOpen && (
                <>
                    <div
                        className="fixed inset-0 z-[110] bg-black/45 backdrop-blur-[2px]"
                        onClick={() => setIsUserModalOpen(false)}
                    />
                    <div
                        role="dialog"
                        aria-modal="true"
                        className="fixed left-4 right-4 top-1/2 -translate-y-1/2 max-w-[380px] mx-auto bg-white dark:bg-[#1a2634] rounded-2xl p-5 shadow-2xl z-[120] border border-slate-200 dark:border-slate-800"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-11 h-11 rounded-full bg-primary/10 text-primary font-bold text-lg flex items-center justify-center shrink-0">
                                {userInitial}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[15px] font-bold text-slate-900 dark:text-white truncate">
                                    {currentUser?.name || 'Usuario'}
                                </div>
                                <div className="text-xs text-slate-500 truncate">
                                    {currentUser?.email || ''}
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 mb-4 border border-slate-200 dark:border-slate-700/60 space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-[11px] font-semibold text-slate-500 uppercase">Rol en Plataforma</span>
                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${currentUser?.globalRole === 'Administrador' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>
                                    {currentUser?.globalRole === 'Administrador' ? 'Administrador' : 'Usuario'}
                                </span>
                            </div>
                            {currentUser?.companyName && (
                                <div className="flex justify-between items-center">
                                    <span className="text-[11px] font-semibold text-slate-500 uppercase">Empresa</span>
                                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[200px]">{currentUser.companyName}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center">
                                <span className="text-[11px] font-semibold text-slate-500 uppercase">Versión App</span>
                                <span className="text-xs font-bold text-primary">{fullVersionString}</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => setIsUserModalOpen(false)}
                            className="w-full py-2.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 transition-colors"
                        >
                            Cerrar
                        </button>
                    </div>
                </>
            )}
        </header>
    );
};

const MobileTabBar = ({ 
    hasPermission, 
    onToggleMenu, 
    isNative, 
    currentUser,
    isPlatformAdmin = false
}: { 
    hasPermission: (module: string, action: 'view'|'edit') => boolean; 
    onToggleMenu: () => void; 
    isNative?: boolean; 
    currentUser?: GlobalUser;
    isPlatformAdmin?: boolean;
}) => {
    const location = useLocation();
    const isActive = (path: string) => location.pathname === path;

    const navItems = [
        { path: "/", label: "Panel", icon: "dashboard" },
        { path: "/planning", label: "Plan", icon: "calendar_month" },
        { path: "/quality", label: "Calidad", icon: "fact_check" },
        { path: "/payments", label: "SC", icon: "handshake" },
    ];

    return (
        <div 
            className={`${isNative ? 'flex' : 'md:hidden flex'} bg-white dark:bg-[#1a2634] border-t border-slate-200 dark:border-slate-800 flex-shrink-0 relative z-40 mt-auto flex-col`}
            style={{
                height: 'calc(56px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))',
                paddingBottom: 'var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px))',
                boxSizing: 'border-box'
            }}
        >
            <div className="flex w-full h-[56px] items-center">
                {navItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`flex-1 h-full py-1 flex flex-col items-center justify-center transition-colors ${isActive(item.path)
                            ? "text-primary"
                            : "text-slate-500 dark:text-slate-400"
                            }`}
                    >
                        <span className="material-symbols-outlined text-[22px] mb-0.5 shrink-0">{item.icon}</span>
                        <span className="text-[10px] font-medium leading-none">{item.label}</span>
                    </Link>
                ))}
            </div>
        </div>
    );
};

const MobileHeaderController = ({
    onBack,
    currentUser,
    onToggleMenu,
    notifications,
    setNotifications,
    companyConfig,
    isNative,
    onLogout
}: {
    onBack: () => void;
    currentUser?: GlobalUser;
    onToggleMenu: () => void;
    notifications?: AppNotification[];
    setNotifications?: React.Dispatch<React.SetStateAction<AppNotification[]>>;
    companyConfig: CompanyConfig;
    isNative?: boolean;
    onLogout?: () => void;
}) => {
    const location = useLocation();
    const isMigrated = isMigratedMobileRoute(location.pathname);

    if (isMigrated) {
        return (
            <div className={`${isNative ? 'block' : 'md:hidden block'} flex-shrink-0 z-40`}>
                <MobileAppHeader
                    onBack={onBack}
                    currentUser={currentUser}
                    onLogout={onLogout}
                    notifications={notifications}
                    setNotifications={setNotifications}
                />
            </div>
        );
    }

    return (
        <MobileHeader
            onBack={onBack}
            currentUser={currentUser}
            onToggleMenu={onToggleMenu}
            notifications={notifications}
            setNotifications={setNotifications}
            companyConfig={companyConfig}
            isNative={isNative}
            onLogout={onLogout}
        />
    );
};

const MobileNavigationController = ({
    hasPermission,
    onToggleMenu,
    isNative,
    currentUser,
    isPlatformAdmin = false
}: {
    hasPermission: (module: string, action: 'view' | 'edit') => boolean;
    onToggleMenu: () => void;
    isNative?: boolean;
    currentUser?: GlobalUser;
    isPlatformAdmin?: boolean;
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMigrated = isMigratedMobileRoute(location.pathname);

    React.useEffect(() => {
        // En móvil, si se intenta acceder a un módulo de escritorio no adaptado (EDT, SSOMA, Inteligencia, etc.), redirigir al panel
        const isMobileEnv = isNative || (typeof window !== 'undefined' && (window.innerWidth < 768 || document.documentElement.classList.contains('is-native') || document.documentElement.classList.contains('kl-mobile')));
        if (isMobileEnv && !isMigrated && location.pathname !== '/select-project') {
            navigate('/', { replace: true });
        }
    }, [location.pathname, isMigrated, isNative, navigate]);

    return (
        <div className={`${isNative ? 'block' : 'md:hidden block'}`}>
            <MobileQuickActions />
            <MobileBottomNav onToggleMenu={onToggleMenu} isPlatformAdmin={isPlatformAdmin} />
        </div>
    );
};

const MobileMenuDrawer = ({ isOpen, onClose, onLogout, hasPermission, isNative }: { isOpen: boolean, onClose: () => void, onLogout: () => void, hasPermission: (module: string, action: 'view'|'edit') => boolean, isNative?: boolean }) => {
    const location = useLocation();
    const { fullVersionString } = useAppVersionDisplay();
    if (!isOpen) return null;

    const navItems = [
        { path: "/", label: "Dashboard", icon: "dashboard", show: true },
        { path: "/edt", label: "EDT (Estructura)", icon: "account_tree", show: hasPermission('mod_edt', 'view') },
        { path: "/planning", label: "Planificación", icon: "calendar_month", show: hasPermission('mod_planificacion', 'view') },
        { path: "/quality", label: "Control de Calidad", icon: "fact_check", show: hasPermission('mod_movil_calidad', 'view') },
        { path: "/payments", label: "Subcontratos/SC", icon: "handshake", show: hasPermission('mod_subcontratos', 'view') },
        { path: "/prevention", label: "Prevención (SSOMA)", icon: "helmet_safety", show: hasPermission('mod_prevencion', 'view') },
        { path: "/data-intelligence", label: "Inteligencia de Datos", icon: "psychology", show: hasPermission('mod_data_intelligence', 'view') },
    ].filter(i => i.show !== false);

    return (
        <div data-mobile-menu-drawer="true" className={`${isNative ? 'fixed' : 'md:hidden fixed'} inset-0 z-[100] flex flex-col bg-white dark:bg-[#1a2634]`}>
            <div 
                className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800"
                style={{
                    paddingTop: "calc(1rem + var(--kl-safe-top, env(safe-area-inset-top, 0px)))"
                }}
            >
                <span className="font-bold text-lg text-slate-800 dark:text-white">Menú Principal</span>
                <button data-drawer-close="true" onClick={onClose} aria-label="Cerrar menú" className="p-2 text-slate-500 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
                    <span className="material-symbols-outlined">close</span>
                </button>
            </div>
            <div className="flex-1 overflow-y-auto py-4 px-4 flex flex-col gap-2">
                {navItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={`flex items-center gap-4 px-4 py-4 rounded-xl transition-colors ${location.pathname === item.path
                            ? "bg-primary text-white"
                            : "bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300"
                            }`}
                    >
                        {item.icon === 'helmet_safety' ? (
                            <HelmetIcon className="size-[24px] flex-shrink-0" />
                        ) : (
                            <span className="material-symbols-outlined text-[24px] flex-shrink-0">{item.icon}</span>
                        )}
                        <span className="font-medium">{item.label}</span>
                    </Link>
                ))}
            </div>
            <div className="p-6 border-t border-slate-200 dark:border-slate-800 pb-safe flex flex-col items-center gap-3">
                <button 
                    onClick={onLogout}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 font-bold rounded-xl"
                >
                    <span className="material-symbols-outlined">logout</span>
                    Cerrar Sesión
                </button>
                <div 
                    className="text-[11px] text-[#94A3B8] text-center font-normal select-none"
                    style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
                >
                    {fullVersionString}
                </div>
            </div>
        </div>
    );
};

const AuthCallbackRedirect = () => {
    const navigate = useNavigate();
    React.useEffect(() => {
        navigate('/', { replace: true });
    }, [navigate]);
    return null;
};

import ReportsScreen from './pages/Reports';
import DataIntelligenceScreen from './pages/DataIntelligence';

const App: React.FC = () => {
    const [isNative] = useState<boolean>(() => {
        try {
            localStorage.removeItem('isNative');
        } catch (e) {}
        const isUrlNative = typeof window !== 'undefined' && (new URLSearchParams(window.location.search).get('native') === '1');
        return Capacitor.isNativePlatform() || isUrlNative || (typeof document !== 'undefined' && (document.documentElement.classList.contains('is-native') || document.documentElement.classList.contains('kl-mobile')));
    });

    // --- Version Update Check State ---
    const [mandatoryUpdate, setMandatoryUpdate] = useState<{ storeUrl: string } | null>(null);
    const [softUpdate, setSoftUpdate] = useState<{ storeUrl: string } | null>(null);

    const runVersionCheck = React.useCallback(async () => {
        try {
            const update = await checkForUpdate();
            if (!update) return;

            if (update.isMandatory) {
                setMandatoryUpdate({ storeUrl: update.storeUrl });
                setSoftUpdate(null);
                return;
            }

            if (update.hasUpdate) {
                // Comprobar si ya se mostró hoy el diálogo de actualización suave (máximo 1 vez al día)
                const lastCheckStr = localStorage.getItem('klyd_last_soft_update_check');
                if (lastCheckStr) {
                    const elapsed = Date.now() - Number(lastCheckStr);
                    if (elapsed < 24 * 60 * 60 * 1000) {
                        return;
                    }
                }
                setSoftUpdate({ storeUrl: update.storeUrl });
            }
        } catch (e) {
            console.warn('[App] Error en verificación de versión (no bloqueante):', e);
        }
    }, []);

    React.useEffect(() => {
        // 1. Al abrir la app
        const timer = setTimeout(() => {
            runVersionCheck();
        }, 1200);

        // 2. Al volver a la app desde segundo plano
        let stateListener: any = null;
        if (Capacitor.isNativePlatform()) {
            CapacitorApp.addListener('appStateChange', (state) => {
                if (state.isActive) {
                    runVersionCheck();
                }
            }).then(handle => {
                stateListener = handle;
            }).catch(() => {});
        }

        return () => {
            clearTimeout(timer);
            if (stateListener && typeof stateListener.remove === 'function') {
                stateListener.remove();
            }
        };
    }, [runVersionCheck]);

    // --- Global State ---

    // Incidences State
    const [incidences, setIncidences] = useSyncState<Incidence[]>('data', 'incidences', []);

    // Projects Multi-support
    const [projects, setProjects] = useSyncState<(ProjectInfo & { id: string })[]>('data', 'projects', []);

    // Company Branding Configuration
    const [companyConfig, setCompanyConfig] = useSyncState<CompanyConfig>('data', 'companyConfig', {
        name: 'Constructora LD',
        rut: '',
        razonSocial: '',
        direccion: '',
        logo: undefined
    });



    const { accounts, instance } = useMsal();
    const currentMsalAccount = accounts[0];

    const [supabaseSession, setSupabaseSession] = useState<any>(null);
    const [checkingSession, setCheckingSession] = useState(true);

    React.useEffect(() => {
        let isDone = false;
        const timer = setTimeout(() => {
            if (!isDone) {
                console.warn('[App] Session check watchdog triggered (5s), unlocking checkingSession');
                setCheckingSession(false);
            }
        }, 5000);

        supabase.auth.getSession().then(({ data: { session } }) => {
            isDone = true;
            clearTimeout(timer);
            setSupabaseSession(session);
            setCheckingSession(false);
        }).catch((err) => {
            console.error('[App] Error getting session:', err);
            isDone = true;
            clearTimeout(timer);
            setCheckingSession(false);
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSupabaseSession(session);
        });

        return () => {
            clearTimeout(timer);
            subscription.unsubscribe();
        };
    }, []);

    const [globalUsers, setGlobalUsers, , usersLoaded, usersLoadedFromServer] = useSyncState<GlobalUser[]>('data', 'globalUsers', []);
    const [globalProfiles, setGlobalProfiles, , profilesLoaded, profilesLoadedFromServer] = useSyncState<Profile[]>('data', 'globalProfiles', []);
    
    // Global Masters
    const [globalCNCGroups, setGlobalCNCGroups] = useSyncState<CNCGroup[]>('data', 'globalCNCGroups', [
        { id: 'g1', name: 'Materiales', causes: [] }
    ]);
    const [globalConstraintGroups, setGlobalConstraintGroups] = useSyncState<ConstraintGroup[]>('data', 'globalConstraintGroups', []);
    const [globalSubcontractTypes, setGlobalSubcontractTypes] = useSyncState<SubcontractType[]>('data', 'globalSubcontractTypes', [
        { id: 'st1', name: 'Suma Alzada' },
        { id: 'st2', name: 'Serie de Precios Unitarios' },
        { id: 'st3', name: 'Administración Delegada' }
    ]);
    const [globalProtocolTemplates, setGlobalProtocolTemplates] = useSyncState<MasterProtocol[]>('data', 'globalProtocolTemplates', [
        {
            id: 'mp-default-1',
            protocolName: 'Protocolo de Hormigonado',
            code: 'PRT-H',
            category: 'Obra Gruesa',
            subcategory: 'Fundaciones',
            items: [
                { id: 'pi1', text: 'Limpieza de superficie y moldajes', additionalInfo: 'Verificar ausencia de escombros' },
                { id: 'pi2', text: 'Trazado y nivelación de cotas', additionalInfo: 'Tolerancia ± 5mm' }
            ]
        }
    ]);
    const [globalSSOMAChecklists, setGlobalSSOMAChecklists] = useSyncState<MasterProtocol[]>('data', 'globalSSOMAChecklists', [
        {
            id: 'sc-default-1',
            protocolName: 'Charla de 5 Minutos',
            code: 'SSOMA-C5M',
            category: 'Charla',
            subcategory: 'Charla General',
            items: [
                { id: 'sci1', text: 'Presentación del tema del día', additionalInfo: 'Explicar riesgos asociados' },
                { id: 'sci2', text: 'Firmas de asistencia', additionalInfo: 'Registrar a todos los participantes' }
            ]
        }
    ]);
    const [globalSSOMAEvents, setGlobalSSOMAEvents] = useSyncState<CNCGroup[]>('data', 'globalSSOMAEvents', [
        {
            id: 'se1',
            name: 'Condición Subestándar',
            causes: [
                { id: 'sec1', name: 'Falta de orden y limpieza' },
                { id: 'sec2', name: 'Iluminación deficiente' }
            ]
        },
        {
            id: 'se2',
            name: 'Acto Subestándar',
            causes: [
                { id: 'sec3', name: 'No usar EPP obligatorio' },
                { id: 'sec4', name: 'Uso inadecuado de herramientas' }
            ]
        }
    ]);
    const [globalSubcontractors, setGlobalSubcontractors] = useSyncState<Subcontractor[]>('data', 'globalSubcontractors', [
        { id: 'sub-seed-1', rut: '76.543.210-K', razonSocial: 'Electricidad Global Ltda.', giro: 'Instalaciones Eléctricas', representante: 'Carlos Saavedra', telefono: '+56912345678', correo: 'contacto@elecglobal.cl', status: 'Activo' },
        { id: 'sub-seed-2', rut: '88.999.111-2', razonSocial: 'Constructora del Sur SpA', giro: 'Movimiento de Tierras', representante: 'Ana María López', telefono: '+56987654321', correo: 'proyectos@consur.cl', status: 'Activo' }
    ]);
    const [globalContracts, setGlobalContracts] = useSyncState<Contract[]>('data', 'globalContracts', []);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Migrate missing default profiles automatically
    React.useEffect(() => {
        if (!profilesLoaded || !usersLoaded) return;
        const defaultProfiles = [
            {
                id: 'prof-admin',
                name: 'Administrador de Obra',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: true }, 'edt_niveles': { view: true, edit: true }, 'edt_editar': { view: true, edit: true }, 'conf_avance_grafico': { view: true, edit: true },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: true }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: true },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: true }, 'ps_revisar': { view: true, edit: true }, 'ps_publicar': { view: true, edit: true }, 'ps_gantt_avance': { view: true, edit: true }, 'ps_eliminar': { view: true, edit: true },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: true, edit: false }, 'sc_config': { view: true, edit: true }, 'sc_crear': { view: true, edit: true }, 'sc_lista': { view: true, edit: true }, 'sc_anadir': { view: true, edit: true }, 'sc_editar': { view: true, edit: true }, 'sc_inactivar': { view: true, edit: true }, 'sc_anular': { view: true, edit: true }, 'sc_activar': { view: true, edit: true }, 'sc_eliminar': { view: true, edit: true }, 'sc_generar_ep': { view: true, edit: true }, 'sc_anexo': { view: true, edit: true }, 'sc_ver_ep': { view: true, edit: true }, 'sc_ep_autorizar': { view: true, edit: true }, 'sc_ep_eliminar': { view: true, edit: true },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_revisar': { view: true, edit: true }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: true },
                    'mod_movil_sc': { view: true, edit: false }, 'movil_sc_ver_ep': { view: true, edit: false }, 'movil_sc_generar_ep': { view: false, edit: true },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false },
                    'mod_data_intelligence': { view: false, edit: false },
                    'di_panel': { view: false, edit: false },
                    'di_kpi': { view: false, edit: false },
                    'di_predictive': { view: false, edit: false },
                    'di_recommendations': { view: false, edit: false },
                    'di_history': { view: false, edit: false },
                    'di_simulations': { view: false, edit: false },
                    'di_config': { view: false, edit: false }
                }
            },
            {
                id: 'prof-jefe',
                name: 'Jefe de Obra (Terreno)',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: false }, 'ps_revisar': { view: true, edit: true }, 'ps_publicar': { view: true, edit: true }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: true, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: false, edit: false }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: false, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: false, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: false, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_revisar': { view: true, edit: true }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: true },
                    'mod_movil_sc': { view: false, edit: false }, 'movil_sc_ver_ep': { view: false, edit: false }, 'movil_sc_generar_ep': { view: false, edit: false },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false },
                    'mod_data_intelligence': { view: false, edit: false },
                    'di_panel': { view: false, edit: false },
                    'di_kpi': { view: false, edit: false },
                    'di_predictive': { view: false, edit: false },
                    'di_recommendations': { view: false, edit: false },
                    'di_history': { view: false, edit: false },
                    'di_simulations': { view: false, edit: false },
                    'di_config': { view: false, edit: false }
                }
            },
            {
                id: 'prof-calidad',
                name: 'Encargado de Calidad',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: false, edit: false }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: false, edit: false }, 'pi_eliminar': { view: false, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: false, edit: false }, 'ps_desglose': { view: false, edit: false }, 'ps_planificaciones': { view: true, edit: false }, 'ps_revisar': { view: true, edit: false }, 'ps_publicar': { view: false, edit: false }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: false, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: false, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: false, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: false, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: false },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_revisar': { view: true, edit: false }, 'movil_ps_tareas': { view: false, edit: false },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: true },
                    'mod_movil_sc': { view: false, edit: false }, 'movil_sc_ver_ep': { view: false, edit: false }, 'movil_sc_generar_ep': { view: false, edit: false },
                    'mod_prevencion': { view: true, edit: false },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false },
                    'mod_data_intelligence': { view: false, edit: false },
                    'di_panel': { view: false, edit: false },
                    'di_kpi': { view: false, edit: false },
                    'di_predictive': { view: false, edit: false },
                    'di_recommendations': { view: false, edit: false },
                    'di_history': { view: false, edit: false },
                    'di_simulations': { view: false, edit: false },
                    'di_config': { view: false, edit: false }
                }
            },
            {
                id: 'prof-oficina',
                name: 'Encargado de Oficina Técnica',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: true }, 'edt_niveles': { view: true, edit: true }, 'edt_editar': { view: true, edit: true }, 'conf_avance_grafico': { view: true, edit: true },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: true, edit: true }, 'pm_gantt_imp': { view: true, edit: true }, 'pm_gantt_exp': { view: true, edit: true }, 'pm_reprog': { view: true, edit: true }, 'pm_historial': { view: true, edit: true }, 'pm_sync': { view: true, edit: true },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: true }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: true },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: true }, 'ps_revisar': { view: true, edit: true }, 'ps_publicar': { view: true, edit: true }, 'ps_gantt_avance': { view: true, edit: true }, 'ps_eliminar': { view: true, edit: true },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: true, edit: false }, 'sc_config': { view: true, edit: true }, 'sc_crear': { view: true, edit: true }, 'sc_lista': { view: true, edit: true }, 'sc_anadir': { view: true, edit: true }, 'sc_editar': { view: true, edit: true }, 'sc_inactivar': { view: true, edit: true }, 'sc_anular': { view: true, edit: true }, 'sc_activar': { view: true, edit: true }, 'sc_eliminar': { view: true, edit: true }, 'sc_generar_ep': { view: true, edit: true }, 'sc_anexo': { view: true, edit: true }, 'sc_ver_ep': { view: true, edit: true }, 'sc_ep_autorizar': { view: true, edit: true }, 'sc_ep_eliminar': { view: true, edit: true },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: true, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_revisar': { view: true, edit: true }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: false },
                    'mod_movil_sc': { view: true, edit: false }, 'movil_sc_ver_ep': { view: true, edit: false }, 'movil_sc_generar_ep': { view: false, edit: true },
                    'mod_prevencion': { view: true, edit: false },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false },
                    'mod_data_intelligence': { view: false, edit: false },
                    'di_panel': { view: false, edit: false },
                    'di_kpi': { view: false, edit: false },
                    'di_predictive': { view: false, edit: false },
                    'di_recommendations': { view: false, edit: false },
                    'di_history': { view: false, edit: false },
                    'di_simulations': { view: false, edit: false },
                    'di_config': { view: false, edit: false }
                }
            },
            {
                id: 'prof-asistente',
                name: 'Asistente de Oficina Técnica',
                permissions: {
                    'mod_incidencias': { view: true, edit: false },
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: false, edit: false }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: false, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: false, edit: false }, 'ps_desglose': { view: false, edit: false }, 'ps_planificaciones': { view: true, edit: false }, 'ps_revisar': { view: true, edit: false }, 'ps_publicar': { view: false, edit: false }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: false, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: true, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: true, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: true, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: true },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_revisar': { view: true, edit: true }, 'movil_ps_tareas': { view: false, edit: true },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: false },
                    'mod_movil_sc': { view: true, edit: false }, 'movil_sc_ver_ep': { view: true, edit: false }, 'movil_sc_generar_ep': { view: false, edit: true },
                    'mod_prevencion': { view: true, edit: false },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false },
                    'mod_data_intelligence': { view: false, edit: false },
                    'di_panel': { view: false, edit: false },
                    'di_kpi': { view: false, edit: false },
                    'di_predictive': { view: false, edit: false },
                    'di_recommendations': { view: false, edit: false },
                    'di_history': { view: false, edit: false },
                    'di_simulations': { view: false, edit: false },
                    'di_config': { view: false, edit: false }
                }
            },
            {
                id: 'prof-prevencion',
                name: 'Prevencionista de Riesgos',
                permissions: {
                    'mod_edt': { view: true, edit: false }, 'edt_unidades': { view: true, edit: false }, 'edt_niveles': { view: true, edit: false }, 'edt_editar': { view: false, edit: false }, 'conf_avance_grafico': { view: true, edit: false },
                    'mod_planificacion': { view: true, edit: false },
                    'mod_plan_maestra': { view: true, edit: false }, 'pm_gantt_edit': { view: false, edit: false }, 'pm_gantt_imp': { view: false, edit: false }, 'pm_gantt_exp': { view: false, edit: false }, 'pm_reprog': { view: false, edit: false }, 'pm_historial': { view: true, edit: false }, 'pm_sync': { view: false, edit: false },
                    'mod_plan_intermedia': { view: true, edit: false }, 'pi_historial': { view: true, edit: false }, 'pi_ite': { view: true, edit: false }, 'pi_generar': { view: false, edit: false }, 'pi_planificaciones': { view: true, edit: false }, 'pi_restricciones': { view: false, edit: false }, 'pi_eliminar': { view: false, edit: false },
                    'mod_plan_semanal': { view: true, edit: false }, 'ps_historial': { view: true, edit: false }, 'ps_crear': { view: false, edit: false }, 'ps_desglose': { view: false, edit: false }, 'ps_planificaciones': { view: true, edit: false }, 'ps_revisar': { view: true, edit: false }, 'ps_publicar': { view: false, edit: false }, 'ps_gantt_avance': { view: false, edit: false }, 'ps_eliminar': { view: false, edit: false },
                    'mod_calidad': { view: true, edit: false }, 'cc_config': { view: false, edit: false }, 'cc_completar': { view: false, edit: false },
                    'mod_subcontratos': { view: false, edit: false }, 'sc_crear': { view: false, edit: false }, 'sc_lista': { view: false, edit: false }, 'sc_anadir': { view: false, edit: false }, 'sc_editar': { view: false, edit: false }, 'sc_inactivar': { view: false, edit: false }, 'sc_anular': { view: false, edit: false }, 'sc_activar': { view: false, edit: false }, 'sc_eliminar': { view: false, edit: false }, 'sc_generar_ep': { view: false, edit: false }, 'sc_anexo': { view: false, edit: false }, 'sc_ver_ep': { view: false, edit: false },
                    'mod_movil': { view: true, edit: false },
                    'mod_panel': { view: true, edit: false }, 'panel_semanal': { view: true, edit: false }, 'panel_intermedia': { view: true, edit: false }, 'panel_ep': { view: false, edit: false }, 'panel_protocolos': { view: true, edit: false },
                    'mod_movil_intermedia': { view: true, edit: false }, 'movil_pi_restricciones': { view: false, edit: false },
                    'mod_movil_semanal': { view: true, edit: false }, 'movil_ps_revisar': { view: false, edit: false }, 'movil_ps_tareas': { view: false, edit: false },
                    'mod_movil_calidad': { view: true, edit: false }, 'movil_cc_todos': { view: true, edit: false }, 'movil_cc_asignados': { view: true, edit: false }, 'movil_cc_completar': { view: false, edit: false },
                    'mod_movil_sc': { view: false, edit: false }, 'movil_sc_ver_ep': { view: false, edit: false }, 'movil_sc_generar_ep': { view: false, edit: false },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false },
                    'mod_data_intelligence': { view: false, edit: false },
                    'di_panel': { view: false, edit: false },
                    'di_kpi': { view: false, edit: false },
                    'di_predictive': { view: false, edit: false },
                    'di_recommendations': { view: false, edit: false },
                    'di_history': { view: false, edit: false },
                    'di_simulations': { view: false, edit: false },
                    'di_config': { view: false, edit: false }
                }
            },
            {
                id: 'prof-innovacion',
                name: 'Equipo Innovación',
                permissions: {
                    'mod_incidencias': { view: true, edit: true },
                    'mod_edt': { view: true, edit: true }, 'edt_unidades': { view: true, edit: true }, 'edt_niveles': { view: true, edit: true }, 'edt_editar': { view: true, edit: true }, 'conf_avance_grafico': { view: true, edit: true },
                    'mod_planificacion': { view: true, edit: true },
                    'mod_plan_maestra': { view: true, edit: true }, 'pm_gantt_edit': { view: true, edit: true }, 'pm_gantt_imp': { view: true, edit: true }, 'pm_gantt_exp': { view: true, edit: true }, 'pm_reprog': { view: true, edit: true }, 'pm_historial': { view: true, edit: true }, 'pm_sync': { view: true, edit: true },
                    'mod_plan_intermedia': { view: true, edit: true }, 'pi_historial': { view: true, edit: true }, 'pi_ite': { view: true, edit: true }, 'pi_generar': { view: true, edit: true }, 'pi_planificaciones': { view: true, edit: true }, 'pi_restricciones': { view: true, edit: true }, 'pi_eliminar': { view: true, edit: true },
                    'mod_plan_semanal': { view: true, edit: true }, 'ps_historial': { view: true, edit: true }, 'ps_crear': { view: true, edit: true }, 'ps_desglose': { view: true, edit: true }, 'ps_planificaciones': { view: true, edit: true }, 'ps_revisar': { view: true, edit: true }, 'ps_publicar': { view: true, edit: true }, 'ps_gantt_avance': { view: true, edit: true }, 'ps_eliminar': { view: true, edit: true },
                    'mod_calidad': { view: true, edit: true }, 'cc_config': { view: true, edit: true }, 'cc_completar': { view: true, edit: true },
                    'mod_subcontratos': { view: true, edit: true }, 'sc_config': { view: true, edit: true }, 'sc_crear': { view: true, edit: true }, 'sc_lista': { view: true, edit: true }, 'sc_anadir': { view: true, edit: true }, 'sc_editar': { view: true, edit: true }, 'sc_inactivar': { view: true, edit: true }, 'sc_anular': { view: true, edit: true }, 'sc_activar': { view: true, edit: true }, 'sc_eliminar': { view: true, edit: true }, 'sc_generar_ep': { view: true, edit: true }, 'sc_anexo': { view: true, edit: true }, 'sc_ver_ep': { view: true, edit: true }, 'sc_ep_autorizar': { view: true, edit: true }, 'sc_ep_eliminar': { view: true, edit: true },
                    'mod_movil': { view: true, edit: true },
                    'mod_panel': { view: true, edit: true }, 'panel_semanal': { view: true, edit: true }, 'panel_intermedia': { view: true, edit: true }, 'panel_ep': { view: true, edit: true }, 'panel_protocolos': { view: true, edit: true },
                    'mod_movil_intermedia': { view: true, edit: true }, 'movil_pi_restricciones': { view: true, edit: true },
                    'mod_movil_semanal': { view: true, edit: true }, 'movil_ps_revisar': { view: true, edit: true }, 'movil_ps_tareas': { view: true, edit: true },
                    'mod_movil_calidad': { view: true, edit: true }, 'movil_cc_todos': { view: true, edit: true }, 'movil_cc_asignados': { view: true, edit: true }, 'movil_cc_completar': { view: true, edit: true },
                    'mod_movil_sc': { view: true, edit: true }, 'movil_sc_ver_ep': { view: true, edit: true }, 'movil_sc_generar_ep': { view: true, edit: true },
                    'mod_prevencion': { view: true, edit: true },
                    'mod_reportes': { view: false, edit: false },
                    'rep_reportes': { view: false, edit: false },
                    'rep_disenar': { view: false, edit: false },
                    'mod_data_intelligence': { view: false, edit: false },
                    'di_panel': { view: false, edit: false },
                    'di_kpi': { view: false, edit: false },
                    'di_predictive': { view: false, edit: false },
                    'di_recommendations': { view: false, edit: false },
                    'di_history': { view: false, edit: false },
                    'di_simulations': { view: false, edit: false },
                    'di_config': { view: false, edit: false }
                }
            }
        ];
        
        // Initialize default profiles ONLY if globalProfiles is completely empty (first-time database setup)
        // Never overwrite existing profiles, custom profiles (like Supervisor), modified names, or customized permissions.
        if (profilesLoadedFromServer && globalProfiles.length === 0) {
            // Verificamos si existe en caché offline para nunca clobber si fue un falso positivo de carga
            const cachedProfiles = localStorage.getItem('klyd_offline_data_globalProfiles');
            if (!cachedProfiles || cachedProfiles === '[]' || cachedProfiles === '{"list":[]}') {
                setGlobalProfiles(defaultProfiles);
            }
        }
        
        // Migrate legacy user roles automatically
        let usersModified = false;
        const currentUsers = [...globalUsers];
        for (let i = 0; i < currentUsers.length; i++) {
            // Convert legacy roles
            if (currentUsers[i].globalRole === 'SYSADMN' as any || currentUsers[i].globalRole === 'SYSADMIN' as any) {
                currentUsers[i] = { ...currentUsers[i], globalRole: 'Administrador' };
                usersModified = true;
            }
        }
        if (usersModified) {
            setGlobalUsers(currentUsers);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profilesLoadedFromServer, usersLoadedFromServer, globalProfiles.length, globalUsers.length]);

    const devBypassedEmail = import.meta.env.DEV ? localStorage.getItem('klyd_bypassed_user_email') : null;
    const devBypassedName = import.meta.env.DEV ? localStorage.getItem('klyd_bypassed_user_name') : null;

    const activeEmail = devBypassedEmail || currentMsalAccount?.username.toLowerCase() || supabaseSession?.user?.email?.toLowerCase();
    const activeName = devBypassedName || currentMsalAccount?.name || supabaseSession?.user?.email?.split('@')[0];

    React.useEffect(() => {
        if (activeEmail && usersLoadedFromServer) {
            const email = activeEmail.trim().toLowerCase();
            // Buscar por correo normalizado ignorando isDeleted para no crear duplicados jamás
            const exists = globalUsers.find(u => (u.email || '').trim().toLowerCase() === email);
            const realId = currentMsalAccount?.localAccountId || supabaseSession?.user?.id;

            if (!exists) {
                // Sin un ID real autenticado, NO se crea el registro (previene registros huérfanos)
                if (!realId) return;

                // Nombramiento seguro: rol 'Usuario', sin permisos admin por prefijo y sin lectura de user_metadata
                setGlobalUsers(prev => [...prev, {
                    id: realId,
                    email: email,
                    name: activeName || email,
                    globalRole: 'Usuario',
                    status: 'Pending',
                    assignedProjects: []
                }]);
            } else {
                // Si está 'Inactive' o isDeleted, no lo toques.
                if (exists.isDeleted || exists.status === 'Inactive') {
                    return;
                }

                // Activación de invitados: si el usuario existe con status: 'Pending' y tiene al menos una obra en assignedProjects
                // (porque lo invitó un administrador o un responsable), cámbialo a 'Active' al iniciar sesión.
                const shouldActivate = exists.status === 'Pending' && (exists.assignedProjects || []).length > 0;

                // Solo actualizar el nombre si viene validado desde Microsoft Entra ID, 
                // o si es un usuario antiguo con el seudónimo 'Usuario Invitado'.
                const hasRealMsalName = !!currentMsalAccount?.name;
                const isLegacyGuest = exists.name === 'Usuario Invitado';
                const shouldUpdateName = activeName && exists.name !== activeName && (hasRealMsalName || isLegacyGuest);

                if (shouldActivate || shouldUpdateName) {
                    setGlobalUsers(prev => prev.map(u => 
                        (u.email || '').trim().toLowerCase() === email 
                            ? { 
                                ...u, 
                                ...(shouldActivate ? { status: 'Active' as const } : {}),
                                ...(shouldUpdateName ? { name: (hasRealMsalName || isLegacyGuest) ? (activeName || u.name) : u.name } : {})
                            } 
                            : u
                    ));
                }
            }
        }
    }, [activeEmail, activeName, globalUsers, setGlobalUsers, currentMsalAccount, supabaseSession, usersLoadedFromServer]);

    const lastValidCurrentUserRef = React.useRef<{ email: string; user: GlobalUser } | null>(null);
    const currentUser = useMemo(() => {
        if (!activeEmail) {
            lastValidCurrentUserRef.current = null;
            return undefined;
        }
        const normalized = activeEmail.trim().toLowerCase();

        // Buscar usuarios coincidentes en globalUsers (disponible de inmediato desde caché local o servidor)
        const matchingUsers = globalUsers.filter(u => 
            (u.email || '').trim().toLowerCase() === normalized && 
            !u.isDeleted && 
            u.status !== 'Inactive'
        );

        if (matchingUsers.length === 0) {
            if (lastValidCurrentUserRef.current && lastValidCurrentUserRef.current.email === normalized) {
                return lastValidCurrentUserRef.current.user;
            }
            return undefined;
        }

        let computed: GlobalUser;
        if (matchingUsers.length === 1) {
            computed = matchingUsers[0];
        } else {
            // Protección defensiva para duplicados históricos: fusionar proyectos y tomar rol más alto
            const base = matchingUsers[matchingUsers.length - 1];
            const allProjectsMap = new Map<string, any>();
            matchingUsers.forEach(u => {
                (u.assignedProjects || []).forEach(ap => {
                    if (!allProjectsMap.has(ap.projectId) || ap.profileId) {
                        allProjectsMap.set(ap.projectId, ap);
                    }
                });
            });
            const isAdmin = matchingUsers.some(u => u.globalRole === 'Administrador' || (u.globalRole as any) === 'SYSADMIN');
            computed = {
                ...base,
                globalRole: (isAdmin ? 'Administrador' : base.globalRole) as any,
                assignedProjects: Array.from(allProjectsMap.values())
            };
        }

        lastValidCurrentUserRef.current = { email: normalized, user: computed };
        return computed;
    }, [globalUsers, activeEmail]);

    React.useEffect(() => {
        const nameToSave = currentUser?.name || activeName;
        if (nameToSave) {
            localStorage.setItem('klyd_current_user_name', nameToSave);
        }
    }, [currentUser, activeName]);

    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => {
        const stored = localStorage.getItem('selectedProjectId');
        if (stored) return stored;
        // Si no hay guardado local, forzamos el default project del usuario actual si existe.
        return currentUser?.defaultProjectId || null;
    });

    const [hasAutoSelected, setHasAutoSelected] = useState(false);

    React.useEffect(() => {
        // En caso que no haya seleccionado nada pero carga por primera vez y tiene un default project:
        if (!selectedProjectId && currentUser?.defaultProjectId && !hasAutoSelected) {
            handleSelectProject(currentUser.defaultProjectId);
            setHasAutoSelected(true);
        } else if (!hasAutoSelected) {
            setHasAutoSelected(true);
        }
    }, [currentUser?.defaultProjectId, selectedProjectId, hasAutoSelected]);

    // Validar acceso al proyecto seleccionado y auto-seleccionar si tiene un único proyecto activo asignado
    React.useEffect(() => {
        if (currentUser) {
            const isSysAdmin = currentUser.globalRole === 'Administrador' || (currentUser.globalRole as any) === 'SYSADMIN';
            
            // Si el usuario no es admin y tiene un proyecto seleccionado al que no tiene acceso, lo deseleccionamos.
            if (!isSysAdmin && selectedProjectId) {
                const hasAccess = currentUser.assignedProjects.some(ap => ap.projectId === selectedProjectId);
                if (!hasAccess) {
                    setSelectedProjectId(null);
                    localStorage.removeItem('selectedProjectId');
                }
            }
            
            // Auto-selección inteligente si no hay proyecto seleccionado y tiene asignado exactamente un proyecto (solo en carga inicial)
            if (!hasAutoSelected && !isSysAdmin && !selectedProjectId && currentUser.assignedProjects && currentUser.assignedProjects.length === 1) {
                const singleProjId = currentUser.assignedProjects[0].projectId;
                const projExists = projects.find(p => p.id === singleProjId && p.status !== 'Inactive');
                if (projExists) {
                    handleSelectProject(singleProjId);
                    setHasAutoSelected(true);
                }
            }
        }
    }, [currentUser, selectedProjectId, projects, hasAutoSelected]);

    const handleSelectProject = (id: string) => {
        setSelectedProjectId(id);
        localStorage.setItem('selectedProjectId', id);
    };

    const handleBackToProjects = React.useCallback(() => {
        setSelectedProjectId(null);
        localStorage.removeItem('selectedProjectId');
        setHasAutoSelected(true);
    }, []);

    React.useEffect(() => {
        const onGoHome = () => handleBackToProjects();
        window.addEventListener('kl-go-home', onGoHome);
        return () => window.removeEventListener('kl-go-home', onGoHome);
    }, [handleBackToProjects]);

    const handleCreateProject = (project: ProjectInfo & { id: string }) => {
        setProjects(prev => [...prev, project]);
        handleSelectProject(project.id);
    };

    const isPlatformAdmin = React.useMemo(() => {
        if (!currentUser) return false;
        if (currentUser.globalRole === 'Administrador' as any || currentUser.globalRole === 'SYSADMIN' as any) {
            return true;
        }
        // Permiso 'admin_plataforma' otorgado vía perfil asignado
        return currentUser.assignedProjects?.some(ap => {
            let profileTarget = ap.profileId || '';
            const profile = globalProfiles.find(p => 
                p.id === profileTarget || 
                p.name === profileTarget || 
                p.name === ap.profileName
            );
            return Boolean(profile?.permissions?.['admin_plataforma']?.view);
        }) ?? false;
    }, [currentUser, globalProfiles]);

    const lastValidUserPermissionsRef = React.useRef<{ key: string; permissions: any } | null>(null);
    const userPermissions = React.useMemo(() => {
        if (!activeEmail) {
            lastValidUserPermissionsRef.current = null;
            return null;
        }
        if (!selectedProjectId) return null;

        const email = activeEmail.trim().toLowerCase();
        const cacheKey = `${email}__${selectedProjectId}`;

        // Si currentUser aún no está listo:
        if (!currentUser) {
            if (lastValidUserPermissionsRef.current?.key === cacheKey) {
                return lastValidUserPermissionsRef.current.permissions;
            }
            return null;
        }

        // Si el usuario está desactivado ('Inactive') o eliminado en el servidor, denegar inmediatamente
        if (currentUser.isDeleted || currentUser.status === 'Inactive') {
            lastValidUserPermissionsRef.current = null;
            return null;
        }

        if (isPlatformAdmin) {
            lastValidUserPermissionsRef.current = { key: cacheKey, permissions: 'ADMIN_ALL' };
            return 'ADMIN_ALL';
        }

        const assignment = currentUser.assignedProjects?.find(ap => ap.projectId === selectedProjectId);
        if (!assignment) {
            if (lastValidUserPermissionsRef.current?.key === cacheKey) {
                return lastValidUserPermissionsRef.current.permissions;
            }
            return null;
        }

        // Si los perfiles globales aún no cargan (lista vacía en memoria):
        if (globalProfiles.length === 0) {
            if (lastValidUserPermissionsRef.current?.key === cacheKey) {
                return lastValidUserPermissionsRef.current.permissions;
            }
            return null;
        }

        // Normalize the profile ID/Name from database to support legacy variants robustly
        let profileTarget = assignment.profileId || '';
        const normalizedTarget = profileTarget.trim().toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // remove accents
            
        if (normalizedTarget === 'encargado de oficina tecnica' || normalizedTarget === 'prof-oficina') {
            profileTarget = 'Encargado de Oficina Técnica';
        } else if (normalizedTarget === 'asistente de oficina tecnica' || normalizedTarget === 'prof-asistente') {
            profileTarget = 'Asistente de Oficina Técnica';
        } else if (normalizedTarget === 'jefe de obra' || (normalizedTarget === 'jefe de obra (terreno)' || normalizedTarget === 'prof-jefe')) {
            profileTarget = 'Jefe de Obra (Terreno)';
        } else if (normalizedTarget === 'encargado de calidad' || normalizedTarget === 'prof-calidad') {
            profileTarget = 'Encargado de Calidad';
        } else if (normalizedTarget === 'prevencionista de riesgos' || normalizedTarget === 'prof-prevencion' || normalizedTarget === 'prevencionista') {
            profileTarget = 'Prevencionista de Riesgos';
        } else if (normalizedTarget === 'administrador de obra' || normalizedTarget === 'prof-admin' || normalizedTarget === 'administrador') {
            profileTarget = 'Administrador de Obra';
        } else if (normalizedTarget === 'equipo innovacion' || normalizedTarget === 'equipo innonvacion' || normalizedTarget === 'prof-innovacion' || normalizedTarget === 'innovacion') {
            profileTarget = 'Equipo Innovación';
        }

        const profile = globalProfiles.find(p => 
            p.id === profileTarget || 
            p.name === profileTarget || 
            p.id === assignment.profileId || 
            p.name === assignment.profileId
        );

        if (!profile) {
            lastValidUserPermissionsRef.current = null;
            return null;
        }

        lastValidUserPermissionsRef.current = { key: cacheKey, permissions: profile.permissions };
        return profile.permissions;
    }, [currentUser, selectedProjectId, globalProfiles, isPlatformAdmin, activeEmail]);

    const hasPermission = React.useCallback((module: string, action: 'view'|'edit') => {
        if (userPermissions === 'ADMIN_ALL') return true;
        if (!userPermissions) return false;
        if (module === 'mod_prevencion' && !isPlatformAdmin) return false; // Restringido temporalmente solo al Administrador de la plataforma
        const modPerms = (userPermissions as any)[module];
        return modPerms ? !!modPerms[action] : false;
    }, [userPermissions, isPlatformAdmin]);

    const currentProject = projects.find(p => p.id === selectedProjectId);

    const collectionName = selectedProjectId ? `project_${selectedProjectId}` : 'data';

    // Project Info
    // Project Info
    const [projectInfo] = useSyncState<ProjectInfo>(collectionName, 'projectInfo', {
        name: 'Cargando...',
        startDate: '2026-01-05',
        endDate: '2026-12-31'
    });

    // Local Tasks
    const [tasks, setTasks, forceSyncTasks] = useSyncState<Task[]>(collectionName, 'tasks', []);

    // EDT Data
    // EDT Data
    const [levels, setLevels] = useSyncState<Level[]>(collectionName, 'levels', []);
    const [units, setUnits] = useSyncState<PhysicalUnit[]>(collectionName, 'units', []);
    // Matrix Data: Key = `taskId_unitId`, Value = string
    // Matrix Data: Key = `taskId_unitId`, Value = string
    const [matrixData, setMatrixData] = useSyncState<Record<string, string>>(collectionName, 'matrixData', {});

    // Notifications state (Secure, isolated per user, backed by user_notifications)
    const { notifications, setNotifications } = useUserNotifications(currentUser);

    // Responsibles Data (Dynamically computed from Global Directory)
    const responsibles: Responsible[] = React.useMemo(() => {
        if (!selectedProjectId) return [];
        return globalUsers
            .filter(u => u.globalRole === 'Administrador' || u.assignedProjects.some(ap => ap.projectId === selectedProjectId))
            .map(u => {
                const assigned = u.assignedProjects.find(ap => ap.projectId === selectedProjectId);
                const matchedProfile = globalProfiles.find(p => p.id === assigned?.profileId || p.name === assigned?.profileId || p.name === assigned?.profileName);
                const roleName = matchedProfile?.name || assigned?.profileName || (u.globalRole === 'Administrador' ? 'Administrador' : 'Usuario');
                return {
                    id: u.id,
                    name: u.name,
                    rut: '',
                    email: u.email,
                    role: roleName,
                    profileId: assigned?.profileId || '',
                    companyName: u.companyName || 'Sin Asignar'
                };
            });
    }, [globalUsers, selectedProjectId, globalProfiles]);
    const setResponsibles = () => { console.warn('Responsibles are dynamically computed from globalUsers and cannot be overridden manually') };


    // Protocol Configuration Data
    // Key = taskId, Value = ProtocolConfig
    const [protocolConfigs, setProtocolConfigs] = useSyncState<Record<string, ProtocolConfig>>(collectionName, 'protocolConfigs', {});
    const [workGroupTemplates, setWorkGroupTemplates] = useSyncState<WorkGroupTemplate[]>(collectionName, 'workGroupTemplates', []);

    // Persistent Protocol State (Moved from QualityScreen)
    const [savedProtocols, setSavedProtocols] = useSyncState<Record<string, ProtocolState>>(collectionName, 'savedProtocols', {});

    // Sincronización automática de fotos pendientes en IndexedDB al recuperar señal o abrir la app
    React.useEffect(() => {
        const cleanup = setupPendingPhotosSync(({ protocolId, itemId, marker, storageUrl }) => {
            setSavedProtocols(prev => {
                const curProto = prev[protocolId];
                if (!curProto) return prev;
                const curChecklist = curProto.checklist || {};
                const curItem = curChecklist[itemId];
                if (!curItem || !curItem.photos) return prev;
                return {
                    ...prev,
                    [protocolId]: {
                        ...curProto,
                        checklist: {
                            ...curChecklist,
                            [itemId]: {
                                ...curItem,
                                photos: curItem.photos.map(p => p === marker ? storageUrl : p)
                            }
                        },
                        updatedAt: new Date().toISOString()
                    }
                };
            });
        });

        return cleanup;
    }, [setSavedProtocols]);

    // Persistent Weekly Plans (Moved from PlanningScreen)
    // Persistent Weekly Plans (Moved from PlanningScreen)
    const [weeklyPlans, setWeeklyPlans] = useSyncState<WeeklyPlan[]>(collectionName, 'weeklyPlans', []);

    // NEW: Persistent Lookahead Plans
    const [lookaheadPlans, setLookaheadPlans] = useSyncState<LookaheadPlan[]>(collectionName, 'lookaheadPlans', []);

    // Persistent Payment Data (Moved from PaymentsScreen)
    const [subcontractors, setSubcontractors] = useSyncState<Subcontractor[]>(collectionName, 'subcontractors', []);

    // Contracts (The Master Agreement)
    const [contracts, setContracts] = useSyncState<Contract[]>(collectionName, 'contracts', []);

    // Payment Statements (The actual Payments generated from Contracts)
    const [paymentStatements, setPaymentStatements] = useSyncState<PaymentStatement[]>(collectionName, 'paymentStatements', []);

    // --- Budget Data State ---
    // --- Budget Data State ---
    const [budgetProjects, setBudgetProjects] = useSyncState<ResourceProject[]>(collectionName, 'budgetProjects', []);
    const [budgetResources, setBudgetResources] = useSyncState<ResourceItem[]>(collectionName, 'budgetResources', []);
    const [budgetItems, setBudgetItems] = useSyncState<BudgetItem[]>(collectionName, 'budgetItems', []);
    const [apuData, setApuData] = useSyncState<Record<string, ApuItem[]>>(collectionName, 'apuData', {});
    const [budgetTree, setBudgetTree] = useSyncState<BudgetFolder[]>(collectionName, 'budgetTree', []);

    // Subcontract Types
    const [subcontractTypes, setSubcontractTypes] = useSyncState<SubcontractType[]>(collectionName, 'subcontractTypes', []);

    // CNC Groups
    const [cncGroups, setCncGroups] = useSyncState<CNCGroup[]>(collectionName, 'cncGroups', [
        {
            id: 'g1',
            name: 'Materiales',
            causes: []
        }
    ]);

    // Constraint Groups
    const [constraintGroups, setConstraintGroups] = useSyncState<ConstraintGroup[]>(collectionName, 'constraintGroups', []);

    // Gantt Baselines History
    const [baselines, setBaselines] = useSyncState<GanttBaseline[]>(collectionName, 'baselines', []);
    const [progressHistory, setProgressHistory] = useSyncState<ProjectProgressSnapshot[]>(collectionName, 'progressHistory', []);

    // Recent Activities
    const [activities, setActivities] = useSyncState<Activity[]>(collectionName, 'activities', []);


    const addActivity = (activity: Omit<Activity, 'id' | 'timestamp' | 'count'>) => {
        // Resolve user dynamically from session/currentUser if it's "Usuario", empty or missing
        let resolvedUser = activity.user;
        const cachedName = typeof window !== 'undefined' ? localStorage.getItem('klyd_current_user_name') : null;
        if (!resolvedUser || resolvedUser === 'Usuario') {
            resolvedUser = currentUser?.name || currentUser?.email || cachedName || 'Usuario';
        }

        const activityWithUser = {
            ...activity,
            user: resolvedUser
        };

        setActivities(prev => {
            const currentActivities = prev || [];
            if (currentActivities.length > 0) {
                const last = currentActivities[0];
                // Group if it has the same title, type and user (sequential)
                if (last.title === activityWithUser.title && last.type === activityWithUser.type && last.user === activityWithUser.user) {
                    const updatedLast: Activity = {
                        ...last,
                        count: (last.count || 1) + 1,
                        timestamp: new Date().toISOString() // Update timestamp to show latest
                    };
                    return [updatedLast, ...currentActivities.slice(1)];
                }
            }

            const newActivity: Activity = {
                ...activityWithUser,
                id: Date.now().toString(),
                timestamp: new Date().toISOString(),
                count: 1
            };
            return [newActivity, ...currentActivities].slice(0, 15);
        });
    };

    const [bypassedWaitingRoom, setBypassedWaitingRoom] = useState(false);

    const handleLogout = async () => {
        try {
            // Limpiar datos temporales o de emulación dev
            if (import.meta.env.DEV) {
                localStorage.removeItem('klyd_bypassed_user_email');
                localStorage.removeItem('klyd_bypassed_user_name');
            }
            localStorage.removeItem('selectedProjectId');
            localStorage.removeItem('klyd_current_user_name');

            // Limpieza completa de sesión de Supabase
            await supabase.auth.signOut().catch(console.error);

            // Si hay cuenta MSAL activa, ejecutar logout de MSAL
            if (currentMsalAccount || accounts.length > 0) {
                clearMsalCacheForce();
                await instance.logoutRedirect({
                    account: currentMsalAccount || accounts[0],
                    postLogoutRedirectUri: window.location.origin
                }).catch(e => console.error("Error en logoutRedirect:", e));
            } else {
                clearMsalCacheForce();
                window.location.href = window.location.origin;
            }
        } catch (error) {
            console.error("Error al cerrar sesión:", error);
            clearMsalCacheForce();
            window.location.href = window.location.origin;
        }
    };

    const isAuthenticated = !!activeEmail;

    if (checkingSession) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0d131b] font-display">
                <div className="animate-pulse text-slate-400 font-bold uppercase tracking-widest text-sm">Restaurando Sesión...</div>
            </div>
        );
    }

    return (
        <>
            {isAuthenticated ? (
                <BrowserRouter>
                    <RouteChangeModalCloser />
                    {!currentUser ? (
                        <div className="min-h-screen flex flex-col gap-4 items-center justify-center bg-slate-50 dark:bg-[#0d131b] font-display text-center p-6">
                            <div className="relative w-16 h-16 flex items-center justify-center mb-2">
                                <span className="material-symbols-outlined text-[40px] text-primary animate-pulse relative z-10">fingerprint</span>
                                <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin border-t-primary"></div>
                            </div>
                            <div className="text-slate-700 dark:text-slate-200 font-bold text-2xl">Validando acceso...</div>
                            <div className="text-slate-500 dark:text-slate-400 text-sm max-w-sm mt-2 mb-4">
                                Estamos verificando tu identidad y recuperando tu perfil.
                            </div>
                        </div>
                    ) : currentUser.status === 'Pending' ? (
                        <div className="min-h-screen flex flex-col gap-4 items-center justify-center bg-slate-50 dark:bg-[#0d131b] font-display text-center p-6">
                            <div className="rounded-2xl bg-orange-100 dark:bg-orange-900/30 p-6 mb-2 mt-4 inline-flex">
                                <span className="material-symbols-outlined text-[64px] text-orange-600 dark:text-orange-400">group_add</span>
                            </div>
                            <div className="text-slate-800 dark:text-slate-100 font-black text-3xl tracking-tight">Sala de Espera</div>
                            <div className="text-slate-500 dark:text-slate-400 text-sm max-w-md mt-2 mb-6 text-center">
                                Tu cuenta de correo ({activeEmail}) ha sido verificada correctamente, pero un Administrador General debe autorizar tu ingreso y asignarte los proyectos correspondientes para operar.
                            </div>
                            <button onClick={handleLogout} className="text-sm font-bold text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 underline underline-offset-4">Cerrar Sesión</button>
                        </div>
                    ) : showAdminPanel && (currentUser.globalRole === 'Administrador' || isPlatformAdmin) ? (
                        <GlobalAdminPanel 
                            onBack={() => setShowAdminPanel(false)}
                            isPlatformAdmin={isPlatformAdmin}
                            globalUsers={globalUsers}
                            setGlobalUsers={setGlobalUsers}
                            globalProfiles={globalProfiles}
                            setGlobalProfiles={setGlobalProfiles}
                            projects={projects}
                            setProjects={setProjects}
                            companyConfig={companyConfig}
                            globalCNCGroups={globalCNCGroups} setGlobalCNCGroups={setGlobalCNCGroups}
                            globalConstraintGroups={globalConstraintGroups} setGlobalConstraintGroups={setGlobalConstraintGroups}
                            globalSubcontractTypes={globalSubcontractTypes} setGlobalSubcontractTypes={setGlobalSubcontractTypes}
                            globalProtocolTemplates={globalProtocolTemplates} setGlobalProtocolTemplates={setGlobalProtocolTemplates}
                            globalSSOMAChecklists={globalSSOMAChecklists} setGlobalSSOMAChecklists={setGlobalSSOMAChecklists}
                            globalSSOMAEvents={globalSSOMAEvents} setGlobalSSOMAEvents={setGlobalSSOMAEvents}
                            incidences={incidences}
                            setIncidences={setIncidences}
                            notifications={notifications}
                            setNotifications={setNotifications}
                            subcontractors={globalSubcontractors}
                            setSubcontractors={setGlobalSubcontractors}
                            contracts={globalContracts}
                            setContracts={setGlobalContracts}
                        />
                    ) : currentUser.globalRole !== 'Administrador' && currentUser.assignedProjects.length === 0 ? (
                        <WaitingRoom 
                            userEmail={activeEmail}
                            isExternal={!activeEmail?.endsWith('@ldconstructora.cl')}
                            onLogout={handleLogout} 
                        />
                    ) : !selectedProjectId ? (
                        <ProjectSelection
                            projects={projects}
                            onSelectProject={handleSelectProject}
                            onCreateProject={handleCreateProject}
                            companyConfig={companyConfig}
                            onUpdateCompanyConfig={setCompanyConfig}
                            currentUser={currentUser}
                            onOpenAdmin={() => setShowAdminPanel(true)}
                            onLogout={handleLogout}
                            notifications={notifications}
                            setNotifications={setNotifications}
                        />
                    ) : (
                        <div className="flex h-screen w-full overflow-hidden bg-background-light dark:bg-background-dark text-[#0d131b] dark:text-white font-display">
                            <Sidebar 
                                onBack={handleBackToProjects} 
                                currentUser={currentUser}
                                onLogout={handleLogout}
                                hasPermission={hasPermission}
                                companyConfig={companyConfig}
                                isNative={isNative}
                                notifications={notifications}
                                setNotifications={setNotifications}
                                isPlatformAdmin={isPlatformAdmin}
                            />
                            <div className="flex-1 overflow-hidden relative flex flex-col">
                                <MobileHeaderController 
                                    onBack={handleBackToProjects} 
                                    currentUser={currentUser}
                                    onToggleMenu={() => setIsMobileMenuOpen(true)}
                                    notifications={notifications}
                                    setNotifications={setNotifications}
                                    companyConfig={companyConfig}
                                    isNative={isNative}
                                    onLogout={handleLogout}
                                />
                                <div className="flex-1 overflow-hidden relative">
                                    <ErrorBoundary>
                                    <Routes>
                                    <Route path="/" element={
                                        <DashboardScreen
                                            projectInfo={currentProject || projectInfo}
                                            tasks={tasks}
                                            responsibles={responsibles}
                                            savedProtocols={savedProtocols}
                                            contracts={contracts}
                                            paymentStatements={paymentStatements}
                                            activities={activities}
                                            progressHistory={progressHistory}
                                            units={units}
                                            matrixData={matrixData}
                                            weeklyPlans={weeklyPlans}
                                            protocolConfigs={protocolConfigs}
                                            companyConfig={companyConfig}
                                            lookaheadPlans={lookaheadPlans}
                                            notifications={notifications}
                                            setNotifications={setNotifications}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                            subcontractors={subcontractors}
                                            isNative={isNative}
                                            baselines={baselines}
                                        />
                                    } />
                                    <Route path="/edt" element={
                                        <EDTScreen
                                            projectInfo={currentProject || projectInfo}
                                            tasks={tasks} setTasks={setTasks}
                                            levels={levels} setLevels={setLevels}
                                            units={units} setUnits={setUnits}
                                            matrixData={matrixData} setMatrixData={setMatrixData}
                                            budgetItems={budgetItems}
                                            apuData={apuData} setApuData={setApuData}
                                            budgetResources={budgetResources}
                                            addActivity={addActivity}
                                            hasPermission={hasPermission}
                                        />
                                    } />
                                    <Route path="/budget" element={
                                        <BudgetScreen
                                            tasks={tasks}
                                            budgetTree={budgetTree} setBudgetTree={setBudgetTree}
                                            budgetItems={budgetItems} setBudgetItems={setBudgetItems}
                                            apuData={apuData} setApuData={setApuData}
                                            budgetResources={budgetResources} setBudgetResources={setBudgetResources}
                                            budgetProjects={budgetProjects} setBudgetProjects={setBudgetProjects}
                                            projectInfo={currentProject || projectInfo}
                                        />
                                    } />
                                    <Route path="/planning" element={
                                        <PlanningScreen
                                            tasks={tasks} setTasks={setTasks}
                                            responsibles={responsibles} setResponsibles={setResponsibles}
                                            globalUsers={globalUsers} setGlobalUsers={setGlobalUsers}
                                            matrixData={matrixData} setMatrixData={setMatrixData}
                                            units={units}
                                            levels={levels}
                                            savedProtocols={savedProtocols}
                                            protocolConfigs={protocolConfigs}
                                            subcontractTypes={subcontractTypes}
                                            setSubcontractTypes={setSubcontractTypes}
                                            weeklyPlans={weeklyPlans}
                                            setWeeklyPlans={setWeeklyPlans}
                                            lookaheadPlans={lookaheadPlans}
                                            setLookaheadPlans={setLookaheadPlans}
                                            projectInfo={currentProject || projectInfo}
                                            cncGroups={cncGroups} setCncGroups={setCncGroups}
                                            constraintGroups={constraintGroups} setConstraintGroups={setConstraintGroups}
                                            globalCNCGroups={globalCNCGroups}
                                            globalConstraintGroups={globalConstraintGroups}
                                            forceSyncTasks={forceSyncTasks}
                                            profiles={globalProfiles}
                                            addActivity={addActivity}
                                            notifications={notifications}
                                            setNotifications={setNotifications}
                                            baselines={baselines}
                                            setBaselines={setBaselines}
                                            progressHistory={progressHistory}
                                            setProgressHistory={setProgressHistory}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                            subcontractors={subcontractors}
                                            contracts={contracts}
                                            companyConfig={companyConfig}
                                            setSavedProtocols={setSavedProtocols}
                                            setProtocolConfigs={setProtocolConfigs}
                                            workGroupTemplates={workGroupTemplates}
                                            setWorkGroupTemplates={setWorkGroupTemplates}
                                        />
                                    } />
                                    <Route path="/quality" element={
                                        <QualityScreen
                                            tasks={tasks}
                                            responsibles={responsibles}
                                            protocolConfigs={protocolConfigs}
                                            setProtocolConfigs={setProtocolConfigs}
                                            units={units}
                                            levels={levels}
                                            matrixData={matrixData}
                                            weeklyPlans={weeklyPlans}
                                            savedProtocols={savedProtocols}
                                            setSavedProtocols={setSavedProtocols}
                                            projectInfo={currentProject || projectInfo}
                                            addActivity={addActivity}
                                            companyConfig={companyConfig}
                                            currentUser={currentUser}
                                            hasPermission={hasPermission}
                                            globalProtocolTemplates={globalProtocolTemplates}
                                            globalProfiles={globalProfiles}
                                            globalUsers={globalUsers}
                                            workGroupTemplates={workGroupTemplates}
                                            setWorkGroupTemplates={setWorkGroupTemplates}
                                        />
                                    } />
                                    <Route path="/payments" element={
                                        <PaymentsScreen
                                            tasks={tasks}
                                            units={units}
                                            levels={levels}
                                            matrixData={matrixData}
                                            protocolConfigs={protocolConfigs}
                                            subcontractors={subcontractors}
                                            setSubcontractors={setSubcontractors}
                                            contracts={contracts}
                                            setContracts={setContracts}
                                            paymentStatements={paymentStatements}
                                            setPaymentStatements={setPaymentStatements}
                                            savedProtocols={savedProtocols}
                                            subcontractTypes={subcontractTypes}
                                            setSubcontractTypes={setSubcontractTypes}
                                            projectInfo={currentProject || projectInfo}
                                            addActivity={addActivity}
                                            companyConfig={companyConfig}
                                            hasPermission={hasPermission}
                                            isPlatformAdmin={userPermissions === 'ADMIN_ALL'}
                                            globalUsers={globalUsers}
                                            globalProfiles={globalProfiles}
                                            responsibles={responsibles}
                                            weeklyPlans={weeklyPlans}
                                            lookaheadPlans={lookaheadPlans}
                                            globalSubcontractors={globalSubcontractors}
                                            setGlobalSubcontractors={setGlobalSubcontractors}
                                            globalContracts={globalContracts}
                                            setGlobalContracts={setGlobalContracts}
                                        />
                                    } />
                                    <Route path="/prevention" element={
                                        hasPermission('mod_prevencion', 'view') ? (
                                            <PreventionScreen
                                                projectInfo={currentProject || projectInfo}
                                                currentUser={currentUser}
                                                hasPermission={hasPermission}
                                                responsibles={responsibles}
                                                addActivity={addActivity}
                                                levels={levels}
                                                units={units}
                                                setNotifications={setNotifications}
                                                globalSSOMAEvents={globalSSOMAEvents}
                                            />
                                        ) : (
                                            <Navigate to="/" replace />
                                        )
                                      } />
                                    <Route path="/data-intelligence" element={
                                        hasPermission('mod_data_intelligence', 'view') ? (
                                             <DataIntelligenceScreen
                                                projectInfo={currentProject || projectInfo}
                                                currentUser={currentUser}
                                                hasPermission={hasPermission}
                                            />
                                         ) : (
                                             <Navigate to="/" replace />
                                         )
                                     } />
                                    <Route path="/reports" element={
                                        hasPermission('mod_reportes', 'view') ? (
                                            <ReportsScreen
                                                projectInfo={currentProject || projectInfo}
                                                currentUser={currentUser}
                                                hasPermission={hasPermission}
                                            />
                                        ) : (
                                            <Navigate to="/" replace />
                                        )
                                    } />
                                    {import.meta.env.DEV && ModalSystemDemo && (
                                        <Route 
                                            path="/modal-demo" 
                                            element={
                                                <React.Suspense fallback={null}>
                                                    <ModalSystemDemo />
                                                </React.Suspense>
                                            } 
                                        />
                                    )}
                                    <Route path="/auth-callback" element={<AuthCallbackRedirect />} />
                                    <Route path="/auth" element={<AuthCallbackRedirect />} />
                                    </Routes>
                                    </ErrorBoundary>
                                    <AIChatWidget currentUser={currentUser} />
                                    <ReportBugWidget currentUser={currentUser} currentProject={currentProject} onAddIncidence={(inc) => setIncidences(prev => [inc, ...prev])} />
                                </div>
                                <MobileNavigationController hasPermission={hasPermission} onToggleMenu={() => setIsMobileMenuOpen(true)} isNative={isNative} currentUser={currentUser} isPlatformAdmin={isPlatformAdmin} />
                                <MobileMenuDrawer 
                                    isOpen={isMobileMenuOpen} 
                                    onClose={() => setIsMobileMenuOpen(false)} 
                                    onLogout={handleLogout} 
                                    hasPermission={hasPermission}
                                    isNative={isNative}
                                />
                            </div>
                        </div>
                    )}
                </BrowserRouter>
            ) : (
                <LoginScreen />
            )}

            {mandatoryUpdate && (
                <MandatoryUpdateScreen storeUrl={mandatoryUpdate.storeUrl} />
            )}

            {softUpdate && (
                <KlDialog
                    open={Boolean(softUpdate)}
                    title="Hay una nueva versión disponible"
                    message="Existe una nueva versión de K-LyDapp en la tienda oficial con mejoras y correcciones. ¿Deseas actualizar ahora?"
                    confirmText="Actualizar"
                    cancelText="Más tarde"
                    onConfirm={async () => {
                        const targetUrl = softUpdate.storeUrl;
                        setSoftUpdate(null);
                        try {
                            await Browser.open({ url: targetUrl });
                        } catch (e) {
                            window.open(targetUrl, '_system');
                        }
                    }}
                    onClose={() => {
                        localStorage.setItem('klyd_last_soft_update_check', String(Date.now()));
                        setSoftUpdate(null);
                    }}
                />
            )}
        </>
    );
};

export default App;
